import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useExamSession } from '@/hooks/useExamSession'
import ExamLayout   from '@/layouts/ExamLayout'
import QuestionCard from '@/components/exam/QuestionCard'

export default function ExamPage() {
  const { code } = useParams()
  const id = code
  const navigate = useNavigate()

  const {
    phase,
    error,
    exam,
    resultMeta,
    questions,
    totalQ,
    currentQ,
    currentIdx,
    answers,
    saveStatus,
    saveAnswer,
    goNext,
    goPrev,
    goToQuestion,
    finish,
  } = useExamSession(id)

  // Redirect to result page once finished, or when attempts are already used up
  useEffect(() => {
    if (phase === 'finished' || phase === 'completed')
      navigate(`/exam/${id}/result`, { replace: true })
  }, [phase, id, navigate])

  if (phase === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f0f2f5] dark:bg-[#161618]">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (phase === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f0f2f5] dark:bg-[#161618] px-6">
        <div className="bg-white dark:bg-[#1e1e22] rounded-2xl border border-gray-200 dark:border-[#2e2e33] p-8 text-center max-w-sm w-full">
          <p className="text-red-500 text-sm mb-4">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
          >
            Bosh sahifaga qaytish
          </button>
        </div>
      </div>
    )
  }

  if (phase === 'finished' || !currentQ) return null

  // Detect subject boundary — show a banner when this is the first question
  // of a new subject block (only relevant when the exam mixes subjects).
  const subjectNames = questions.map(q => q.subject_name ?? q.question_bank_name ?? null)
  const uniqueSubjects = new Set(subjectNames.filter(Boolean))
  const isFirstOfSubject = currentIdx === 0 || subjectNames[currentIdx] !== subjectNames[currentIdx - 1]
  const showSubjectBanner = uniqueSubjects.size > 1 && isFirstOfSubject && subjectNames[currentIdx]
  const subjectQuestionCount = subjectNames.filter(n => n === subjectNames[currentIdx]).length

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <ExamLayout
        exam={exam}
        resultMeta={resultMeta}
        totalQ={totalQ}
        currentIdx={currentIdx}
        answers={answers}
        saveStatus={saveStatus}
        saveAnswer={saveAnswer}
        questions={questions}
        onFinish={finish}
        onPrev={goPrev}
        onNext={goNext}
        onJump={goToQuestion}
        onExpire={finish}
      >
        {showSubjectBanner && (
          <div className="mb-4 flex items-center gap-2.5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/50 rounded-xl px-4 py-3">
            <span className="text-lg">📘</span>
            <div>
              <p className="text-sm font-bold text-blue-700 dark:text-blue-300">{subjectNames[currentIdx]}</p>
              <p className="text-xs text-blue-500 dark:text-blue-400">{subjectQuestionCount} ta savol</p>
            </div>
          </div>
        )}
        <QuestionCard
          question={currentQ}
          questionNumber={currentIdx + 1}
          value={answers[currentQ.id]}
          saveStatus={saveStatus[currentQ.id]}
          onChange={(val) => saveAnswer(currentQ.id, val)}
        />
      </ExamLayout>
    </div>
  )
}
